// ============================================================================
// TREND ANALYSIS — MAIN COMPONENT
//
// Standalone tab for period-over-period comparison.
// The user defines two time periods; the orchestrator runs the process miner
// twice to snapshot each period's data; a single AI call produces the delta
// analysis rendered by periodComparison-renderer.js.
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { getAIModelConfig, getInvokeAIProvider } from '@advisor-core/lib/aiModelConfig';
import { invokeWebAI } from '@advisor-core/lib/webAiClient';
import { TrendingUp, Calendar } from 'lucide-react';
import {
  getAppVersion, getIssueTypes, getProcessModelTitle, getModelID, getModelDateRange, getActiveTimeFilter, getAiConsultantContent,
  taskJSON, getAttributePrivacyRules,
} from '@process-advisor/process-miner/processMiner/processMiner';

import { openProgressModal }   from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal }  from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';
import { updateProgressModal } from '@process-advisor/process-miner/api/methods/modals/updateProgressModal';
import { startAiProgressTimer, stopAiProgressTimer, stopAllAiProgressTimers } from '@process-advisor/process-miner/consultant/aiProgressModalTimer';
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';

import { buildPeriodComparisonPrompt } from '@process-advisor/process-miner/consultant/periodComparison-prompt';
import { renderPeriodComparisonHTML, buildPeriodComparisonExportHTML } from '@process-advisor/process-miner/consultant/periodComparison-renderer';
import { runPeriodComparison }         from '@process-advisor/process-miner/consultant/periodComparisonOrchestrator';

import { replaceQuotes, sleep } from '@process-advisor/process-miner/dashboard/aiinsights/aiInsights-utils';
import { saveConsultantContent } from '@process-advisor/process-miner/consultant/saveConsultantContent';
import {
  getCachedConsultantContent, setCachedConsultantContent,
  isHtmlCurrentVersion,
  getCachedConsultantJson, setCachedConsultantJson,
} from '@process-advisor/process-miner/consultant/consultantContentState';
import HtmlPreviewFrame   from '@process-advisor/process-miner/consultant/HtmlPreviewFrame';
import ExportModal        from '@process-advisor/process-miner/consultant/ExportModal';
import { themeAiHtml, appDefaultReportTheme } from '@process-advisor/process-miner/consultant/reportThemeUtils';
import ConsultantQaPanel, { AskAiButton } from '@process-advisor/process-miner/consultant/ConsultantQaPanel';
import ConsultantTabHeader, { ReadyBadge, HEADER_META_TEXT_STYLE, getHeaderPrimaryButtonStyle, getHeaderSecondaryButtonStyle } from '@process-advisor/process-miner/consultant/ConsultantTabHeader';
import { SaveSnapshotButton, SaveSnapshotModal } from '@process-advisor/process-miner/consultant/ConsultantSnapshotManager';
import { saveSnapshot } from '@process-advisor/process-miner/consultant/consultantSnapshotService';
import { injectOrganizationContext } from '@process-advisor/process-miner/consultant/organizationProfile';
import useAiReportScopeControls from '@process-advisor/process-miner/consultant/useAiReportScopeControls';
import { applyAiReportScopeToEvidence } from '@process-advisor/process-miner/consultant/aiReportScope';
import { invokeProcessOpenAIWithBilling } from '@process-advisor/process-miner/consultant/aiBillingAccess';

// ── Forge lazy loader ──────────────────────────────────────────────────────────
let forgeModule = null;
const loadForgeModule = async () => {
  if (!forgeModule) forgeModule = await import('@forge/bridge');
  return forgeModule;
};

const toInvokeProvider = (p) =>
  p === 'gemini' ? 'Gemini' : p === 'claude' ? 'Claude' : 'ChatGPT';

const SLEEP_MS   = 50;
const POLL_MS    = 1000;
const COL        = 'ai_trend_analysis';

const TREND_ANALYSIS_RESPONSE_SCHEMA = {
  schema: {
    type: 'object',
    properties: {
      comparison_summary: {
        type: 'object',
        properties: {
          headline: { type: 'string' },
          leadership_summary: { type: 'string' },
          period_baseline_label: { type: 'string' },
          period_comparison_label: { type: 'string' },
        },
        required: ['headline', 'leadership_summary', 'period_baseline_label', 'period_comparison_label'],
      },
      overall_verdict: {
        type: 'object',
        properties: {
          verdict: { type: 'string', enum: ['Significantly Improved', 'Improved', 'Stable', 'Deteriorated', 'Significantly Deteriorated'] },
          confidence: { type: 'string', enum: ['High', 'Moderate', 'Low'] },
          verdict_rationale: { type: 'string' },
        },
        required: ['verdict', 'confidence', 'verdict_rationale'],
      },
      metric_deltas: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            metric: { type: 'string' },
            category: { type: 'string', enum: ['duration', 'rework', 'efficiency', 'volume', 'complexity', 'quality'] },
            baseline_value: { type: 'string' },
            comparison_value: { type: 'string' },
            absolute_delta: { type: 'string' },
            percent_change: { type: 'string' },
            direction: { type: 'string', enum: ['improved', 'degraded', 'unchanged', 'unavailable'] },
            significance: { type: 'string', enum: ['high', 'medium', 'low'] },
            why_it_matters: { type: 'string' },
          },
          required: ['metric', 'category', 'baseline_value', 'comparison_value', 'absolute_delta', 'percent_change', 'direction', 'significance', 'why_it_matters'],
        },
      },
      transition_changes: {
        type: 'object',
        properties: {
          notable_improvements: { type: 'array', items: { type: 'object', properties: { transition: { type: 'string' }, baseline_avg_hrs: { type: 'string' }, comparison_avg_hrs: { type: 'string' }, delta_hrs: { type: 'string' }, interpretation: { type: 'string' } } } },
          notable_deteriorations: { type: 'array', items: { type: 'object', properties: { transition: { type: 'string' }, baseline_avg_hrs: { type: 'string' }, comparison_avg_hrs: { type: 'string' }, delta_hrs: { type: 'string' }, interpretation: { type: 'string' } } } },
          appeared_in_comparison: { type: 'array', items: { type: 'string' } },
          disappeared_in_comparison: { type: 'array', items: { type: 'string' } },
          rework_volume_change: { type: 'string' },
        },
      },
      variant_changes: {
        type: 'object',
        properties: {
          dominant_path_stable: { type: 'boolean' },
          dominant_path_baseline: { type: 'string' },
          dominant_path_comparison: { type: 'string' },
          variant_count_baseline: { type: 'integer' },
          variant_count_comparison: { type: 'integer' },
          rework_variant_share_baseline: { type: 'string' },
          rework_variant_share_comparison: { type: 'string' },
          summary: { type: 'string' },
        },
      },
      improvement_signals: { type: 'array', items: { type: 'object', properties: { title: { type: 'string' }, metric_or_transition: { type: 'string' }, baseline_value: { type: 'string' }, comparison_value: { type: 'string' }, delta: { type: 'string' }, why_it_matters: { type: 'string' } } } },
      deterioration_signals: { type: 'array', items: { type: 'object', properties: { title: { type: 'string' }, metric_or_transition: { type: 'string' }, baseline_value: { type: 'string' }, comparison_value: { type: 'string' }, delta: { type: 'string' }, why_it_matters: { type: 'string' } } } },
      no_deterioration: { type: 'boolean' },
      stable_signals: { type: 'array', items: { type: 'object', properties: { title: { type: 'string' }, value: { type: 'string' }, interpretation: { type: 'string' } } } },
      hypotheses: { type: 'array', items: { type: 'object', properties: { title: { type: 'string' }, hypothesis: { type: 'string' }, supporting_evidence: { type: 'string' }, confidence: { type: 'string', enum: ['High', 'Moderate', 'Low'] } } } },
      data_coverage: {
        type: 'object',
        properties: {
          metrics_available: { type: 'boolean' },
          transitions_available: { type: 'boolean' },
          variants_available: { type: 'boolean' },
          field_usage_available: { type: 'boolean' },
          status_model_available: { type: 'boolean' },
          time_intelligence_available: { type: 'boolean' },
          sample_items_available: { type: 'boolean' },
          notes: { type: 'array', items: { type: 'string' } },
        },
      },
    },
    required: ['comparison_summary', 'overall_verdict', 'metric_deltas', 'transition_changes', 'variant_changes', 'improvement_signals', 'deterioration_signals', 'no_deterioration', 'stable_signals', 'hypotheses', 'data_coverage'],
  },
};

let pollCtr = 0;

const cleanAndParseJSON = (raw) => {
  let s = String(raw ?? '').trim();

  // Remove markdown fences if the model wrapped the JSON.
  s = s
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/```\s*$/i, '')
    .trim();

  // Fast path: already valid JSON.
  try { return JSON.parse(s); } catch (_) {}

  // If the response includes explanatory text before the JSON, slice from the
  // first object/array token and trim to the last closing token.
  const firstObj = s.indexOf('{');
  const firstArr = s.indexOf('[');
  let start = -1;

  if (firstObj === -1) start = firstArr;
  else if (firstArr === -1) start = firstObj;
  else start = Math.min(firstObj, firstArr);

  if (start === -1) {
    console.error('[TrendAnalysis] No JSON start found in AI response.', { raw });
    throw new Error('Trend Analysis returned no JSON content.');
  }

  s = s.slice(start);

  const lastObj = s.lastIndexOf('}');
  const lastArr = s.lastIndexOf(']');
  const end = Math.max(lastObj, lastArr);
  if (end !== -1) s = s.slice(0, end + 1);

  try { return JSON.parse(s); } catch (_) {}

  // Final recovery attempt for truncated JSON.
  const opens = [];
  for (const ch of s) {
    if (ch === '{' || ch === '[') opens.push(ch);
    else if (ch === '}' || ch === ']') opens.pop();
  }

  let recovered = s.replace(/,?\s*"[^"]*"?\s*:?\s*[^,\}\]]*$/, '');
  for (let i = opens.length - 1; i >= 0; i--) recovered += opens[i] === '{' ? '}' : ']';

  try {
    return JSON.parse(recovered);
  } catch (err) {
    console.error('[TrendAnalysis] Failed to parse AI JSON.', {
      raw,
      cleaned: s,
      recovered,
      err,
    });
    throw err;
  }
};

// ── Date helpers ───────────────────────────────────────────────────────────────
const today    = () => new Date().toISOString().slice(0, 10);
const toFilter = (iso) => iso.replaceAll('-', '/');
const fmtDate  = (d) => d.toISOString().slice(0, 10);

/**
 * Derive default period A and period B from the model's date range.
 * Splits the full range at its midpoint:
 *   Period A (baseline):   model.from  →  midpoint
 *   Period B (comparison): midpoint+1  →  model.to
 * Falls back to rolling 6m/3m windows if no model date range is available.
 */
const getDefaultPeriods = () => {
  const range = getModelDateRange();
  if (range?.from && range?.to) {
    const fromMs  = new Date(range.from).getTime();
    const toMs    = new Date(range.to).getTime();
    const midMs   = Math.floor((fromMs + toMs) / 2);
    const mid     = fmtDate(new Date(midMs));
    const midNext = fmtDate(new Date(midMs + 86400000)); // mid + 1 day
    return {
      a: { from: range.from, to: mid,      label: '' },
      b: { from: midNext,    to: range.to, label: '' },
    };
  }
  // Fallback: rolling windows
  const d = new Date();
  const mo = (n) => { const x = new Date(d); x.setMonth(x.getMonth() - n); return fmtDate(x); };
  return {
    a: { from: mo(6), to: mo(3), label: '' },
    b: { from: mo(3), to: today(), label: '' },
  };
};

const inputStyle = {
  height: 32, border: '1px solid #DFE1E6', borderRadius: 4,
  padding: '0 10px', fontSize: 13, background: '#fff', width: '100%',
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
};
const labelStyle = {
  fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.07em', color: '#6B778C', marginBottom: 4,
};

// ── Main component ─────────────────────────────────────────────────────────────

const TrendAnalysis = ({ processLabel = 'Process', entityLabel = 'Work Item', refreshKey, aiModelConfig }) => {

  const modelId = getModelID();

  // ── State ──────────────────────────────────────────────────────────────────
  const [isGenerating, setIsGenerating]     = useState(false);
  const [generated, setGenerated]           = useState(false);
  const [savedHtml, setSavedHtml]           = useState(null);
  const [savedJson, setSavedJson]           = useState(null);
  const [generationTime, setGenerationTime] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [snapshotModalOpen, setSnapshotModalOpen] = useState(false);
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [qaOpen, setQaOpen] = useState(false);

  const defaults = getDefaultPeriods();
  const [periodA, setPeriodA] = useState(defaults.a);
  const [periodB, setPeriodB] = useState(defaults.b);

  // Forge poll state
  const [requestId, setRequestId]   = useState(null);
  const [forgeStatus, setForgeStatus] = useState(null);
  const pollRef = useRef(null);
  const aiTimerRef = useRef(null);
  const aiProgressTimerRef = useRef(null);

  const aiScope = useAiReportScopeControls({
    modelId,
    reportKey: 'trendAnalysis',
    reportLabel: 'Trend Analysis',
    getTaskRows: taskJSON,
    getVariants: () => apiProvider.variantsTable() || [],
    getProcessSteps: () => apiProvider.processStepsTable() || [],
    getPrivacyRules: getAttributePrivacyRules,
  });

  // ── Load cache on mount — session cache first, then DB ─────────────────────
  useEffect(() => {
    const cached = getCachedConsultantContent({ modelId, column: COL });
    const dbHtml = getAiConsultantContent ? getAiConsultantContent(COL) : null;
    const saved = cached || (isHtmlCurrentVersion(dbHtml) ? dbHtml : null);
    if (saved) {
      setSavedHtml(saved);
      setGenerated(true);
      if (!cached) setCachedConsultantContent({ modelId, column: COL, html: saved });
      const cachedJson = getCachedConsultantJson({ modelId, column: COL });
      if (cachedJson) setSavedJson(cachedJson);
    }
  }, [modelId]);

  // ── Forge poll loop ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!requestId || !(forgeStatus?.status === 'pending' || forgeStatus?.status === 'processing')) return;

    pollRef.current = setInterval(async () => {
      try {
        const { invoke } = await loadForgeModule();
        const sd = await invoke('checkStatus', { requestId });
        setForgeStatus(sd);
        pollCtr += 1;
        updateProgressModal({ status: 'Analysing trend data — ' + pollCtr + 's', ai: true });

        if (sd?.status === 'completed') {
          clearInterval(pollRef.current);
          const raw  = sd?.result ?? '{}';
          console.log('[TrendAnalysis] Raw poll result:', raw);
          const json = cleanAndParseJSON(typeof raw === 'string' ? raw : JSON.stringify(raw));
          replaceQuotes(json);
          setGenerationTime(pollCtr.toString());
          const html = themeAiHtml(renderPeriodComparisonHTML(json, getProcessModelTitle()), appDefaultReportTheme());
          setSavedHtml(html); setSavedJson(json); setGenerated(true);
          setCachedConsultantContent({ modelId, column: COL, html });
          setCachedConsultantJson({ modelId, column: COL, json });
          saveConsultantContent({ modelId, column: COL, html, AppVersion: getAppVersion() });
          setIsGenerating(false); closeProgressModal();
        }
        if (sd?.status === 'failed') {
          clearInterval(pollRef.current);
          setIsGenerating(false); closeProgressModal();
        }
      } catch (err) {
        console.error('[TrendAnalysis] Poll error:', err);
        clearInterval(pollRef.current);
        setIsGenerating(false); closeProgressModal();
      }
    }, POLL_MS);

    return () => pollRef.current && clearInterval(pollRef.current);
  }, [requestId, forgeStatus?.status]);

  // ── Validation ─────────────────────────────────────────────────────────────
  const valid =
    periodA.from && periodA.to && periodA.from < periodA.to &&
    periodB.from && periodB.to && periodB.from < periodB.to;

  const labelA = periodA.label?.trim() || `${periodA.from} – ${periodA.to}`;
  const labelB = periodB.label?.trim() || `${periodB.from} – ${periodB.to}`;

  // ── Generate ────────────────────────────────────────────────────────────────
  const handleSaveSnapshot = async (description) => {
    setSnapshotSaving(true);
    try {
      const html = savedHtml || null;
      if (!html) return;
      const result = await saveSnapshot({
        modelId, tab: 'trendAnalysis', tabLabel: 'Trend Analysis',
        description, html, AppVersion: getAppVersion(),
      });
      if (!result?.success) throw new Error('Snapshot was not saved');
      setSnapshotModalOpen(false);
    } catch (error) {
      console.error('Advisor snapshot save failed', error);
      window.alert(`Unable to save Advisor Snapshot: ${error.message || error}`);
    } finally {
      setSnapshotSaving(false);
    }
  };

  const generate = async () => {
    if (isGenerating || !valid) return;
    stopAllAiProgressTimers();
    pollCtr = 0;
    if (aiTimerRef.current) { clearInterval(aiTimerRef.current); aiTimerRef.current = null; }
    setIsGenerating(true);
    setSavedHtml(null); setSavedJson(null);
    setGenerated(false); setGenerationTime(null);

    const AppVersion  = getAppVersion();
    const issueTypes   = getIssueTypes();
    const issueTypeStr = issueTypes.length === 1
      ? `Work Type = ${issueTypes[0]}`
      : `Work Types = ${issueTypes.join(' or ')}`;

    await openProgressModal({ status: 'Starting trend analysis…' });
    await sleep(SLEEP_MS);

    try {
      // Step 1: two-pass data capture
      const activeFilter = getActiveTimeFilter();
      const { snapshotA, snapshotB } = await runPeriodComparison({
        periodA: { from: toFilter(periodA.from), to: toFilter(periodA.to), label: labelA },
        periodB: { from: toFilter(periodB.from), to: toFilter(periodB.to), label: labelB },
        onProgress: (msg) => updateProgressModal({ status: msg }),
        originalFilter: activeFilter,
      });

      const scopeSnapshot = (snapshot) => {
        const scoped = applyAiReportScopeToEvidence({
          rawSampleItems: snapshot.sampleItems || [],
          processSteps: snapshot.transitionsJSON || [],
          variants: snapshot.variantsJSON || [],
          scope: aiScope.aiReportScope,
        });
        return {
          ...snapshot,
          sampleItems: scoped.rawSampleItems,
          transitionsJSON: scoped.processSteps,
          variantsJSON: scoped.variants,
          reportScopeSummary: scoped.summary,
        };
      };
      const scopedSnapshotA = scopeSnapshot(snapshotA);
      const scopedSnapshotB = scopeSnapshot(snapshotB);

      // Step 2: build prompt
      await updateProgressModal({ status: 'Sending to AI for analysis…' });
      const prompt = await injectOrganizationContext(buildPeriodComparisonPrompt({ snapshotA: scopedSnapshotA, snapshotB: scopedSnapshotB, issueTypeStr, reportScopeSummary: aiScope.getReportScopeSummary() }));
      const { provider, model, temperature } = getAIModelConfig(aiModelConfig);

      // Step 3: AI call
      if (AppVersion === 'FORGE') {
        const { invoke } = await loadForgeModule();
        const response   = await invokeProcessOpenAIWithBilling({
          invoke,
          reportLabel: 'Trend Analysis',
          payload: { prompt, aiProvider: toInvokeProvider(provider), model, temperature, schema: TREND_ANALYSIS_RESPONSE_SCHEMA },
        });
        setRequestId(response.requestId);
        setForgeStatus({ status: 'pending' });
        // Poll loop takes over from here
        return;
      }

      // Web SaaS path
      if (AppVersion === 'WEB') {
        aiProgressTimerRef.current=await startAiProgressTimer('Analysing trend data');
        const result=await invokeWebAI({
          prompt,
          reportLabel:'Trend Analysis',
          temperature,
          responseFormat:'json',
          modelId,
        });
        const json=cleanAndParseJSON(result?.text||'{}');
        replaceQuotes(json);
        const html=themeAiHtml(renderPeriodComparisonHTML(json,getProcessModelTitle()),appDefaultReportTheme());
        setSavedHtml(html);setSavedJson(json);setGenerated(true);
        setCachedConsultantContent({modelId,column:COL,html});
        setCachedConsultantJson({modelId,column:COL,json});
        saveConsultantContent({modelId,column:COL,html,AppVersion});
        stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current=null;
        if (aiTimerRef.current) { clearInterval(aiTimerRef.current); aiTimerRef.current=null; }
        setIsGenerating(false);closeProgressModal();
        return;
      }

      // Direct API path
      if (AppVersion === 'SNOW') {
      const _geminiKey = (typeof API_KEY !== 'undefined' && API_KEY) ? API_KEY : undefined;
        aiProgressTimerRef.current = await startAiProgressTimer('Analysing trend data');
        const res  = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${_geminiKey}`,
          {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contents: [{ parts: [{ text: prompt + '\n\nYou must output valid JSON. Do not include ``` at beginning and end' }] }],
              generationConfig: { responseMimeType: 'application/json', responseSchema: TREND_ANALYSIS_RESPONSE_SCHEMA.schema, maxOutputTokens: 32768 },
            }),
          }
        );
        const data = await res.json();
        const json = cleanAndParseJSON(data?.candidates?.[0]?.content?.parts?.[0]?.text || '{}');
        replaceQuotes(json);
        const html = themeAiHtml(renderPeriodComparisonHTML(json, getProcessModelTitle()), appDefaultReportTheme());
        setSavedHtml(html); setSavedJson(json); setGenerated(true);
        setCachedConsultantContent({ modelId, column: COL, html });
        setCachedConsultantJson({ modelId, column: COL, json });
        saveConsultantContent({ modelId, column: COL, html, AppVersion });
        stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null;
        if (aiTimerRef.current) { clearInterval(aiTimerRef.current); aiTimerRef.current = null; }
        setIsGenerating(false); closeProgressModal();
      }

    } catch (err) {
      console.error('[TrendAnalysis] Error:', err);
      stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null;
      if (aiTimerRef.current) { clearInterval(aiTimerRef.current); aiTimerRef.current = null; }
      setIsGenerating(false); closeProgressModal();
    }
  };

  // ── Export handlers ─────────────────────────────────────────────────────────
  const handleExportHTML = (themeId = appDefaultReportTheme()) => {
    const html = savedJson
      ? themeAiHtml(buildPeriodComparisonExportHTML(savedJson, getProcessModelTitle()), themeId)
      : savedHtml;
    if (!html) return;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `trend-analysis-${(getProcessModelTitle() || 'report').replace(/\s+/g, '-').toLowerCase()}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setIsExportModalOpen(false);
  };

  const handleExportPDF = (themeId = appDefaultReportTheme()) => {
    const html = savedJson
      ? themeAiHtml(buildPeriodComparisonExportHTML(savedJson, getProcessModelTitle()), themeId)
      : savedHtml;
    if (!html) return;
    const win = window.open('', '_blank', 'width=1400,height=900');
    if (!win) return;
    win.document.open(); win.document.write(html); win.document.close();
    win.focus(); setTimeout(() => win.print(), 300);
    setIsExportModalOpen(false);
  };

  const handleExportJSON = () => {
    if (!savedJson) return;
    const blob = new Blob([JSON.stringify(savedJson, null, 2)], { type: 'application/json;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `trend-analysis-${(getProcessModelTitle() || 'report').replace(/\s+/g, '-').toLowerCase()}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setIsExportModalOpen(false);
  };

  const handleExportPPTX = async (themeId = appDefaultReportTheme()) => {
    const contentJson = savedJson;
    if (!contentJson) return;
    try {
      // Lazy import keeps pptxgenjs out of the main bundle until first use.
      const { buildAiContentPptx } = await import('@process-advisor/process-miner/consultant/aiContent-pptx');
      const pptx = buildAiContentPptx('periodComparison', contentJson, getProcessModelTitle(), { theme: themeId });
      await pptx.writeFile({ fileName: `trend-analysis-${(getProcessModelTitle() || 'report').replace(/\s+/g, '-').toLowerCase()}.pptx` });
    } catch (error) {
      console.error('PPTX export failed', error);
    }
    setIsExportModalOpen(false);
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ backgroundColor: '#f8f9fa', padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>

        {/* ── Header bar ── */}
        <ConsultantTabHeader
          title="Trend Analysis"
          subtitle="Period-over-period comparison — what improved, what deteriorated, and why"
          icon={<TrendingUp size={20} color="white" />}
          iconBackground="linear-gradient(135deg, #0052CC 0%, #2684FF 100%)"
          titleColor="#0052CC"
          actions={(
            <>
              {!generated && <ReadyBadge color="#0C66E4" />}
              {aiScope.controls}
              {generated && (
                <>
                  <AskAiButton hasContent={generated} onClick={() => setQaOpen(true)} />
                  <button
                    style={getHeaderPrimaryButtonStyle('#0052CC')}
                    onClick={() => { setSavedHtml(null); setGenerated(false); }}
                  >
                    New Analysis
                  </button>
                  <SaveSnapshotButton color="#0052CC" onClick={() => setSnapshotModalOpen(true)} />
                  <button
                    style={getHeaderSecondaryButtonStyle('#0C66E4')}
                    onClick={() => setIsExportModalOpen(true)}
                  >
                    Export...
                  </button>
                </>
              )}
            </>
          )}
        />

        {aiScope.panels}

        {/* ── Pre-generate panel ── */}
        {!generated && (
          <div style={{
            background: '#fff', padding: '28px 32px 32px', margin: '0 10px 10px',
            borderRadius: '0 0 8px 8px', border: '2px solid #dfe1e6', borderTop: 'none',
          }}>
            {/* Icon + title */}
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <div style={{
                width: 56, height: 56, margin: '0 auto 16px',
                background: 'linear-gradient(135deg, #0C66E4 0%, #2684FF 100%)',
                borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <TrendingUp size={28} color="white" />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#172b4d' }}>Run Trend Analysis</h3>
              <p style={{ margin: 0, fontSize: 14, color: '#5e6c84', lineHeight: 1.6, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
                Define two time periods. The process miner extracts each period's data in a few seconds
                and the AI produces a structured delta — what improved, what deteriorated, and what likely drove the change.
              </p>
            </div>

            {/* Warning — only shown when a time filter is currently active */}
            {getActiveTimeFilter() && (
              <div style={{
                marginBottom: 24, padding: '10px 14px', background: '#FFF0B3',
                border: '1px solid #FFE380', borderRadius: 6, fontSize: 12, color: '#974F0C', lineHeight: 1.55,
              }}>
                ⚠ A time filter is currently active. Running the analysis will temporarily override it, then restore it when complete. This takes a few seconds.
              </div>
            )}

            {/* Validation errors */}
            {periodA.from && periodA.to && periodA.from >= periodA.to && (
              <div style={{ marginBottom: 8, padding: '6px 10px', background: '#FFEBE6', border: '1px solid #FF8F73', borderRadius: 6, fontSize: 12, color: '#BF2600' }}>
                Baseline: "From" must be before "To"
              </div>
            )}
            {periodB.from && periodB.to && periodB.from >= periodB.to && (
              <div style={{ marginBottom: 8, padding: '6px 10px', background: '#FFEBE6', border: '1px solid #FF8F73', borderRadius: 6, fontSize: 12, color: '#BF2600' }}>
                Comparison: "From" must be before "To"
              </div>
            )}

            {/* Date pickers */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 28 }}>

              {/* Period A — Baseline */}
              <div style={{ flex: 1, minWidth: 280, border: '1px solid #85B7EB', borderRadius: 8, padding: '16px 18px', background: '#F0F7FF' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
                  <Calendar size={14} color="#0052CC" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0052CC' }}>Baseline period</span>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 110 }}>
                    <div style={labelStyle}>From</div>
                    <input type="date" style={inputStyle} value={periodA.from} max={periodA.to || today()} onChange={(e) => setPeriodA(p => ({ ...p, from: e.target.value }))} />
                  </div>
                  <div style={{ flex: 1, minWidth: 110 }}>
                    <div style={labelStyle}>To</div>
                    <input type="date" style={inputStyle} value={periodA.to} min={periodA.from} max={today()} onChange={(e) => setPeriodA(p => ({ ...p, to: e.target.value }))} />
                  </div>
                  <div style={{ flex: 1, minWidth: 110 }}>
                    <div style={labelStyle}>Label (optional)</div>
                    <input type="text" placeholder="e.g. Q1 2025" style={inputStyle} value={periodA.label} onChange={(e) => setPeriodA(p => ({ ...p, label: e.target.value }))} />
                  </div>
                </div>
              </div>

              {/* VS divider */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 32 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#97A0AF' }}>VS</span>
              </div>

              {/* Period B — Comparison */}
              <div style={{ flex: 1, minWidth: 280, border: '1px solid #B3D4FF', borderRadius: 8, padding: '16px 18px', background: '#E9F2FF' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
                  <Calendar size={14} color="#0747A6" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0747A6' }}>Comparison period</span>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 110 }}>
                    <div style={labelStyle}>From</div>
                    <input type="date" style={inputStyle} value={periodB.from} max={periodB.to || today()} onChange={(e) => setPeriodB(p => ({ ...p, from: e.target.value }))} />
                  </div>
                  <div style={{ flex: 1, minWidth: 110 }}>
                    <div style={labelStyle}>To</div>
                    <input type="date" style={inputStyle} value={periodB.to} min={periodB.from} max={today()} onChange={(e) => setPeriodB(p => ({ ...p, to: e.target.value }))} />
                  </div>
                  <div style={{ flex: 1, minWidth: 110 }}>
                    <div style={labelStyle}>Label (optional)</div>
                    <input type="text" placeholder="e.g. Q2 2025" style={inputStyle} value={periodB.label} onChange={(e) => setPeriodB(p => ({ ...p, label: e.target.value }))} />
                  </div>
                </div>
              </div>
            </div>

            {/* Run button */}
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={generate}
                disabled={!valid || isGenerating}
                style={{
                  background: (!valid || isGenerating) ? '#dfe1e6' : 'linear-gradient(135deg, #0C66E4 0%, #2684FF 100%)',
                  color: (!valid || isGenerating) ? '#97a0af' : '#fff',
                  border: 'none', padding: '10px 32px', borderRadius: 6,
                  fontSize: 15, fontWeight: 600,
                  cursor: (!valid || isGenerating) ? 'not-allowed' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  boxShadow: (!valid || isGenerating) ? 'none' : '0 2px 6px rgba(12,102,228,0.3)',
                }}
              >
                <TrendingUp size={18} />
                {isGenerating ? 'Running analysis…' : 'Run Trend Analysis'}
              </button>
              {!valid && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#6B778C' }}>
                  Complete both periods above to enable the analysis.
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Output ── */}
        {generated && savedHtml && (
          <div style={{ margin: '0 10px 0' }}>
            <HtmlPreviewFrame html={savedHtml} title="Trend Analysis" />
          </div>
        )}

        <div style={{ height: 16 }} />
      </div>

      <ConsultantQaPanel
        isOpen={qaOpen}
        onClose={() => setQaOpen(false)}
        tabId="trend-analysis"
        tabLabel="Trend Analysis"
        themeColor="#0C66E4"
        resultData={savedJson}
        savedHtml={savedHtml}
        generated={generated}
        modelTitle={getProcessModelTitle()}
        processLabel={processLabel}
        entityLabel={entityLabel}
        aiModelConfig={aiModelConfig}
      />

      <SaveSnapshotModal
        isOpen={snapshotModalOpen}
        onClose={() => setSnapshotModalOpen(false)}
        onSave={handleSaveSnapshot}
        tab="trendAnalysis"
        tabLabel="Trend Analysis"
        saving={snapshotSaving}
      />
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        title="Export Trend Analysis"
        onExportHTML={handleExportHTML}
        onExportPDF={handleExportPDF}
        onExportJSON={handleExportJSON} onExportPPTX={handleExportPPTX}
      />
    </div>
  );
};

export default TrendAnalysis;
